/**
 * Admin Users Zustand Store
 *
 * Manages all state for the Admin Users section including:
 * - Admin user list with filters and pagination
 * - CRUD operations for admin users
 * - Password reset functionality
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  SuperAdminUser,
  SuperAdminUserListItem,
  SuperAdminRole,
  PaginationMeta,
  AdminUserFilters,
  CreateSuperAdminUserDto,
  UpdateSuperAdminUserDto,
  ResetPasswordDto,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface AdminUsersError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface AdminUsersState {
  // List
  users: SuperAdminUserListItem[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: AdminUsersError | null;

  // Filters
  filters: AdminUserFilters;

  // Active User (for edit modal)
  activeUser: SuperAdminUser | null;
  isLoadingUser: boolean;

  // Action States
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isResettingPassword: boolean;
  actionError: AdminUsersError | null;

  // Actions
  loadUsers: (page?: number) => Promise<void>;
  refreshUsers: () => Promise<void>;
  setFilter: <K extends keyof AdminUserFilters>(key: K, value: AdminUserFilters[K]) => void;
  clearFilters: () => void;
  loadUser: (id: string) => Promise<void>;
  clearActiveUser: () => void;
  createUser: (dto: CreateSuperAdminUserDto) => Promise<string | null>;
  updateUser: (id: string, dto: UpdateSuperAdminUserDto) => Promise<boolean>;
  deleteUser: (id: string) => Promise<boolean>;
  resetPassword: (id: string, dto?: ResetPasswordDto) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // List
  users: [] as SuperAdminUserListItem[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as AdminUsersError | null,

  // Filters
  filters: {} as AdminUserFilters,

  // Active User
  activeUser: null as SuperAdminUser | null,
  isLoadingUser: false,

  // Action States
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isResettingPassword: false,
  actionError: null as AdminUsersError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useAdminUsersStore = create<AdminUsersState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadUsers: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      if (filters.search) params.set('search', filters.search);
      if (filters.role) params.set('role', filters.role);
      if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));

      const response = await api.get<{
        data: SuperAdminUserListItem[];
        pagination: PaginationMeta;
      }>(`${API_ENDPOINTS.SUPER_ADMIN.USERS.LIST}?${params.toString()}`);

      set({
        users: response.data,
        pagination: response.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_USERS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load admin users',
        },
      });
    }
  },

  refreshUsers: async () => {
    const { pagination } = get();
    await get().loadUsers(pagination?.page ?? 1);
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadUsers(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadUsers(1);
  },

  // ============================================
  // Single User Actions
  // ============================================

  loadUser: async (id: string) => {
    set({ isLoadingUser: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: SuperAdminUser;
      }>(API_ENDPOINTS.SUPER_ADMIN.USERS.GET(id));

      set({
        activeUser: response.data,
        isLoadingUser: false,
      });
    } catch (error) {
      set({
        isLoadingUser: false,
        actionError: {
          code: 'LOAD_USER_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load admin user',
        },
      });
    }
  },

  clearActiveUser: () => {
    set({ activeUser: null });
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createUser: async (dto: CreateSuperAdminUserDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const user = await api.post<SuperAdminUser>(
        API_ENDPOINTS.SUPER_ADMIN.USERS.CREATE,
        dto
      );

      set({ isCreating: false });
      await get().refreshUsers();
      return user.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_USER_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create user',
        },
      });
      return null;
    }
  },

  updateUser: async (id: string, dto: UpdateSuperAdminUserDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.USERS.UPDATE(id), dto);

      set({ isUpdating: false });
      await get().refreshUsers();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_USER_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update user',
        },
      });
      return false;
    }
  },

  deleteUser: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.USERS.DELETE(id));

      // Remove from local state
      set((state) => ({
        users: state.users.filter((u) => u.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_USER_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete user',
        },
      });
      return false;
    }
  },

  resetPassword: async (id: string, dto?: ResetPasswordDto): Promise<boolean> => {
    set({ isResettingPassword: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.USERS.RESET_PASSWORD(id), dto ?? {});

      set({ isResettingPassword: false });
      return true;
    } catch (error) {
      set({
        isResettingPassword: false,
        actionError: {
          code: 'RESET_PASSWORD_FAILED',
          message: error instanceof Error ? error.message : 'Failed to reset password',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({
      listError: null,
      actionError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get count of active users */
export const useActiveUserCount = () => {
  return useAdminUsersStore((state) => state.users.filter((u) => u.isActive).length);
};

/** Get users grouped by role */
export const useUsersByRole = () => {
  return useAdminUsersStore((state) => {
    const byRole: Record<SuperAdminRole, SuperAdminUserListItem[]> = {
      SuperAdmin: [],
      SupportAdmin: [],
      BillingAdmin: [],
      ComplianceAdmin: [],
      ReadOnlyAdmin: [],
    };
    state.users.forEach((u) => {
      byRole[u.role].push(u);
    });
    return byRole;
  });
};

/** Get count of users by role */
export const useUserCountByRole = (role: SuperAdminRole) => {
  return useAdminUsersStore((state) => state.users.filter((u) => u.role === role).length);
};

/** Check if any admin users data is loading */
export const useAdminUsersLoading = () => {
  const isLoadingList = useAdminUsersStore((state) => state.isLoadingList);
  const isLoadingUser = useAdminUsersStore((state) => state.isLoadingUser);
  const isCreating = useAdminUsersStore((state) => state.isCreating);
  const isUpdating = useAdminUsersStore((state) => state.isUpdating);
  const isDeleting = useAdminUsersStore((state) => state.isDeleting);
  return isLoadingList || isLoadingUser || isCreating || isUpdating || isDeleting;
};

/** Get active filters count */
export const useAdminUserFiltersCount = () => {
  return useAdminUsersStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.role) count++;
    if (state.filters.isActive !== undefined) count++;
    return count;
  });
};
